import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { currentPage, PageType } from "@/lib/utils"
import { AppDispatch } from "@/store"
import { useLocation } from "react-router-dom"
import { useAuthReady } from "./use-auth-ready"
import { initApp } from "@/store/init/initThunks"
import { initProgressTracker } from "@/store/progressTracker/progressTrackerThunks"

/**
 * Pages that fetch their own data (orders, subscriptions, users, exams, etc.)
 * manage their own guarded mount effects — see their respective components.
 * This hook only covers the source/topics tree and progress-tracker data,
 * which several pages read but never fetch themselves; both thunks below
 * are self-guarding (they skip the network call if already loaded), so it's
 * safe to dispatch them unconditionally on every visit to these pages.
 */
export const usePageBasedApiLoader = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { pathname } = useLocation()
  const authReady = useAuthReady()

  useEffect(() => {
    if (!authReady) return
    const page = currentPage(pathname)

    switch (page) {
      case PageType.QuestionBank:
      case PageType.PracticeTestBuilder:
      case PageType.SourceManagement:
      case PageType.InterestManagement:
      case PageType.FlaggedQuestions:
        dispatch(initApp(false))
        break
      case PageType.ProgressTracker:
        dispatch(initProgressTracker())
        break
      default:
        break
    }
  }, [dispatch, pathname, authReady])
}
